import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Wallet, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const { company_name, app_name, tagline, logoUrl } = useBranding();
  const brandName = company_name || app_name;
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-paper">
      <div className="hidden md:flex flex-col justify-between w-1/2 bg-ink text-paper p-12">
        <div className="flex items-center gap-3 min-w-0">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="h-10 w-10 shrink-0 rounded-md object-contain bg-paper" />
          ) : (
            <div className="h-10 w-10 shrink-0 rounded-md bg-paper text-ink flex items-center justify-center">
              <Wallet size={20} strokeWidth={1.75} />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-heading text-xl font-bold truncate">{brandName}</div>
            {tagline && <div className="label-eyebrow text-paper/70 truncate">{tagline}</div>}
          </div>
        </div>
        <div className="space-y-4">
          {tagline ? (
            <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-[1.05]">{tagline}</h1>
          ) : (
            // No tagline set — a neutral, non-branded headline (not a placeholder, not left blank) that
            // still names the product via app_name rather than any hardcoded literal.
            <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-[1.05]">
              Sign in to<br />
              continue to<br />
              <span className="text-paper-alt">{app_name}.</span>
            </h1>
          )}
          <p className="text-paper/70 text-sm max-w-md">
            A disciplined daily cash book. Record opening balance, log every voucher, and watch your
            running balance — the way a ledger is meant to be kept.
          </p>
        </div>
        <div className="label-eyebrow text-paper/50 truncate">© {brandName}</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="label-eyebrow mb-2">Sign in</div>
            <h2 className="font-heading text-3xl font-bold">Welcome back</h2>
            <p className="text-sm text-muted mt-1">Use your credentials to access the ledger.</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="label-eyebrow">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                data-testid="login-email"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password" className="label-eyebrow">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password"
                className="mt-1.5"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full bg-ink hover:bg-ink-soft text-white"
          >
            {loading ? "Signing in…" : (<span className="inline-flex items-center gap-2">Sign in <ArrowRight size={16} /></span>)}
          </Button>
        </form>
      </div>
    </div>
  );
}
